import { NgClass } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { PreMedia } from '@app/features/pre-media/pre-media.adapter';
import { ArticleModel } from '@modules/article/models/article.model';
import { MediaModelType } from '@modules/media/models/media.model';
import { IcsModalComponent } from '@modules/shared/modal/ics-modal/ics-modal.component';
import { DialogService } from '@modules/shared/services/dialog.service';
import { LightboxService } from '@modules/shared/services/lightbox.service';
import { WindowService } from '@modules/shared/services/window.service';
import {
  ActionIcons,
  ButtonItem,
  ActionContainerComponent,
} from '@modules/ui/action-container/action-container.component';
import { MetaItemInterface, MetaItemsComponent } from '@modules/ui/meta-items/meta-items.component';

import { ContactPersonCardComponent } from '../card/contact-person-card/contact-person-card.component';
import { DocumentCardComponent } from '../card/document-card/document-card.component';
import { AudioComponent } from '../media/audio/audio.component';
import { ImageComponent } from '../media/image/image.component';
import { LocalizedDatePipe } from '../pipes/localized-date.pipe';
import { ParallaxDirective } from '../shared/directives/parallax.directive';
import { ScrollSpyDirective } from '../shared/scroll-spy/scroll-spy.directive';
import { AccordionComponent } from '../ui/accordion/accordion.component';
import { InfoComponent } from '../ui/info/info.component';

import { ArchivedBannerComponent } from './archived-banner/archived-banner.component';
import { ArticleGalleryComponent } from './article-gallery/article-gallery.component';
import { ArticleStatusBarComponent } from './article-status-bar/article-status-bar.component';
import { ExpandableCopyComponent } from './expandable-copy/expandable-copy.component';
import { MasonryGalleryComponent } from './masonry-gallery/masonry-gallery.component';

@Component({
  selector: 'mb-article',
  templateUrl: './article.component.html',
  styleUrls: ['./article.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ImageComponent,
    ParallaxDirective,
    InfoComponent,
    ScrollSpyDirective,
    NgClass,
    RouterLink,
    AccordionComponent,
    MetaItemsComponent,
    ActionContainerComponent,
    ExpandableCopyComponent,
    ArticleGalleryComponent,
    MasonryGalleryComponent,
    DocumentCardComponent,
    AudioComponent,
    ContactPersonCardComponent,
    ArticleStatusBarComponent,
    ArchivedBannerComponent,
    LocalizedDatePipe,
    TranslatePipe,
  ],
})
export class ArticleComponent implements OnInit, OnChanges, OnDestroy {
  private lightboxService = inject(LightboxService);
  private windowService = inject(WindowService);
  private dialogService = inject(DialogService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  public headlineContainerClasses = ['article-section-container', 'article-section'];
  public headlineClass = ['article-headline'];
  public buttonHubClass = ['article-button-hub-section'];

  public activePressKitArticleId!: string;
  public nextPressKitArticleId!: string;
  @Input() public pressKitArticles!: ArticleModel[];
  public showPressKitArticles = false;

  public readonly pressKitArticleType = 'presskit_article';
  public readonly pressKitArticleDefaultActions: ButtonItem[] = [
    {
      icon: ActionIcons.SHARE,
      class: 'btn btn-plain d-none d-sm-inline-flex',
    },
  ];

  public destroy$ = new Subject<boolean>();

  @Input() article!: ArticleModel;
  @Input() preMedia?: PreMedia;

  @Input() defaultActions: ButtonItem[] = [];
  @Input() articleActions: ButtonItem[] = [];
  @Input() sectionActions: ButtonItem[] = [];

  @Output() actionClick = new EventEmitter<{ action: ActionIcons; resource: any }>();

  ngOnInit(): void {
    if (this.article.type === this.pressKitArticleType) {
      this.setPressKitArticles();
      this.setPressKitArticleHeadlineClass();
      this.setPressKitArticleHeadlineContainerClasses();
      this.setActiveAndNextPressKitIds();
      this.setPressKitArticleButtonHubClass();
    }
  }

  ngOnChanges(): void {
    if (this.article.type === this.pressKitArticleType) {
      this.togglePressKitArticlesAccordion(false);
      this.setActiveAndNextPressKitIds();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public openIcsModal() {
    const ref = this.dialogService.open({
      component: IcsModalComponent,
      inputs: {
        icsLink: this.preMedia?.ics_link,
      },
      outputs: {
        confirm: () => {
          ref.close();
          // download ics file
        },
        requestCancel: () => {
          ref.close();
        },
      },
    });
  }

  public openMediaGallery(list: MediaModelType[], index: number) {
    this.lightboxService.openMediaGallery(list, index, this.getCurrentRelationship(), this.preMedia);
  }

  public scrollToAnchor(metaItem: MetaItemInterface) {
    this.windowService.scrollToId(metaItem.data);
  }

  public goToNextArticle(): void {
    this.router.navigate([`../${this.nextPressKitArticleId}`], { relativeTo: this.activatedRoute });
  }

  public togglePressKitArticlesAccordion(isOpen: boolean): void {
    this.showPressKitArticles = isOpen;
  }

  private setPressKitArticles(): void {
    this.activatedRoute.parent?.data.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data.pressKit) {
        this.pressKitArticles = data.pressKit.articles;
      }
    });
  }

  private setActiveAndNextPressKitIds(): void {
    if (this.pressKitArticles) {
      this.activePressKitArticleId = this.pressKitArticles[this.pressKitArticles.indexOf(this.article)]?.id;
      this.nextPressKitArticleId = this.pressKitArticles[this.pressKitArticles.indexOf(this.article) + 1]?.id;
    }
  }

  private setPressKitArticleHeadlineClass(): void {
    if (!this.article.headerImage) {
      this.headlineClass = ['article-headline-press'];
    }
  }

  private setPressKitArticleHeadlineContainerClasses(): void {
    this.headlineContainerClasses = ['article-section-container', 'press-article-headline-container'];
  }

  private setPressKitArticleButtonHubClass(): void {
    this.buttonHubClass = ['press-article-button-hub-section'];
  }

  private getCurrentRelationship() {
    return {
      label: this.preMedia ? 'CONNECTED_PREMEDIA' : 'CONNECTED_ARTICLE',
      text: this.article.title,
      link: {
        url: this.preMedia ? `/pre-media/${this.preMedia.id}` : `/article/${this.article.id}`,
      },
    };
  }
}
